import { QueryCache, QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { STATUS_CODES } from 'constant/http';
import { Logger } from 'utils/logger';
import { serializeError } from 'utils/observability/serializeError';

/**
 * Query keys whose 404 response should propagate to the nearest
 * NotFoundBoundary rather than being swallowed as isError state.
 * Add a key here when a new detail page needs a not-found route.
 */
const NOT_FOUND_BOUNDARY_KEYS = new Set([
  'billDetail', // Bills detail view
  // singleBill (Bills edit) handled per-component — inner ErrorBoundary wraps AddEditViewComponent
  // projectDetail / draftProjectDetail handled per-component — pages/Project entry has no NotFoundBoundary
  'formBuilderDetail', // Custom Form builder
]);

// SECURITY: Always pass errors through serializeError before logging.
// AxiosError.toJSON() — called automatically by the OTel SDK when serializing log body values — dumps the full Axios request config, which includes the Authorization header containing the user's JWT Bearer token. Passing the raw error would log live credentials to SigNoz, visible to anyone with read access.
// serializeError reads only safe fields: name, message, stack, status,
// statusText, url, method, and responseData. The request config (and its headers) is never included.
export const queryCache = new QueryCache({
  onError: (error) => {
    Logger.error({
      message: 'Query Cache Error',
      data: serializeError(error),
    });
  },
});

/**
 * Create a new query client instance
 */
export const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      staleTime: 20 * 1000, // 20 seconds
      gcTime: 30 * 1000, // 30 seconds
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = (error as AxiosError)?.response?.status;
        // Avoid retrying for client errors (400-499)
        if (
          status &&
          status >= STATUS_CODES.BadRequest &&
          status < STATUS_CODES.InternalServerError
        )
          return false;
        return failureCount < 2;
      },
      throwOnError: (error, query) => {
        if ((error as AxiosError)?.response?.status !== 404) return false;
        const key = Array.isArray(query.queryKey)
          ? String(query.queryKey[0])
          : '';
        return NOT_FOUND_BOUNDARY_KEYS.has(key);
      },
    },
    mutations: {
      onError(error) {
        Logger.error({
          message: 'Mutation Error',
          data: serializeError(error),
        });
      },
    },
  },
});
